import { useCallback, useMemo } from 'react';
import { SearchFilters } from '../types/ngap';
import { trackFilterApplied } from '../utils/analyticsEvents';

import { useAppContext } from '../store';

interface UseFiltersReturn {
  availableData: {
    lettres_cles: string[];
    themes: string[];
    reeducations: string[];
    available_vignettes: string[];
    cotationRange: { min: number; max: number };
  };
  handleFiltersChange: (newFilters: SearchFilters) => void;
  handleVignetteToggle: (vignette: string) => void;
  handleClearAllVignettes: () => void;
}

export const useFilters = (): UseFiltersReturn => {
  const { state, dispatch } = useAppContext();
  const { acts } = state.data;
  const { searchQuery, filters } = state.searchAndFilter;

  // Calculate available data for filters
  const availableData = useMemo(() => {
    // Helper function to get acts filtered by all criteria except the specified one
    const getActsFilteredByOtherCriteria = (excludeFilter: string) => {
      let result = [...acts];

      // Apply search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        result = result.filter(act => {
          const searchableText = [
            act.libelle,
            act.lettre_cle,
            act.cotation_valeur,
            act.theme,
            act.reeducation,
            act.sous_division,
            act.pathologies
          ].filter(Boolean).join(' ').toLowerCase();
          
          return searchableText.includes(query);
        });
      }

      // Apply filters except the excluded one
      result = result.filter(act => {
        // Letter key filter
        if (excludeFilter !== 'lettre_cle' && filters.lettre_cle && act.lettre_cle !== filters.lettre_cle) {
          return false;
        }

        // Theme filter
        if (excludeFilter !== 'theme' && filters.theme && act.theme !== filters.theme) {
          return false;
        }

        // Reeducation filter
        if (excludeFilter !== 'reeducation' && filters.reeducation && act.reeducation !== filters.reeducation) {
          return false;
        }

        // Referentiel filter
        if (excludeFilter !== 'soumis_referentiel' && filters.soumis_referentiel !== 'all') {
          const isReferentiel = filters.soumis_referentiel === 'true';
          if (act.soumis_referentiel !== isReferentiel) {
            return false;
          }
        }

        // Vignettes filter
        if (excludeFilter !== 'selected_vignettes' && filters.selected_vignettes && filters.selected_vignettes.length > 0) {
          const actVignettes = new Set([
            act.vignette_label_0,
            act.vignette_label_1,
            act.vignette_label_2,
            act.vignette_label_3,
            act.vignette_label_4
          ].filter(Boolean));

          const hasAllSelectedVignettes = filters.selected_vignettes.every(selectedVignette => 
            actVignettes.has(selectedVignette)
          );
          
          if (!hasAllSelectedVignettes) {
            return false;
          }
        }

        return true;
      });

      return result;
    };

    // Calculate dynamic options based on filtered data
    const lettres_cles = [...new Set(getActsFilteredByOtherCriteria('lettre_cle').map(act => act.lettre_cle).filter(Boolean))].sort();
    const themes = [...new Set(getActsFilteredByOtherCriteria('theme').map(act => act.theme).filter(Boolean))].sort();
    const reeducations = [...new Set(getActsFilteredByOtherCriteria('reeducation').map(act => act.reeducation).filter(Boolean))].sort();
    
    // Calculate available vignettes from acts filtered by other criteria
    let available_vignettes: string[] = [];
    
    if (filters.selected_vignettes.length === 0) {
      // Si aucune vignette n'est sélectionnée, montrer toutes les vignettes disponibles
      available_vignettes = [...new Set(
        getActsFilteredByOtherCriteria('selected_vignettes').flatMap(act => [
          act.vignette_label_0,
          act.vignette_label_1,
          act.vignette_label_2,
          act.vignette_label_3,
          act.vignette_label_4
        ].filter(Boolean))
      )];
    } else {
      // Si des vignettes sont sélectionnées, ne montrer que les vignettes compatibles
      const actsWithSelectedVignettes = getActsFilteredByOtherCriteria('selected_vignettes').filter(act => {
        const actVignettes = new Set([
          act.vignette_label_0,
          act.vignette_label_1,
          act.vignette_label_2,
          act.vignette_label_3,
          act.vignette_label_4
        ].filter(Boolean));
        
        // L'acte doit avoir TOUTES les vignettes sélectionnées
        return filters.selected_vignettes.every(selectedVignette => 
          actVignettes.has(selectedVignette)
        );
      });
      
      // Récupérer toutes les vignettes des actes compatibles
      available_vignettes = [...new Set(
        actsWithSelectedVignettes.flatMap(act => [
          act.vignette_label_0,
          act.vignette_label_1,
          act.vignette_label_2,
          act.vignette_label_3
        ].filter(Boolean))
      )];
    }

    return {
      lettres_cles,
      themes,
      reeducations,
      available_vignettes,
      cotationRange: { min: 0, max: 100 }
    };
  }, [acts, searchQuery, filters]);

  const handleFiltersChange = useCallback((newFilters: SearchFilters) => {
    dispatch({ type: 'SET_FILTERS', payload: newFilters });
    
    // Reset to first page when filters change
    dispatch({ type: 'SET_CURRENT_PAGE', payload: 1 });
    
    // Track filter usage
    trackFilterApplied('filters', 'applied');
  }, [dispatch]);

  const handleVignetteToggle = useCallback((vignette: string) => {
    const newFilters = {
      ...filters,
      selected_vignettes: filters.selected_vignettes.includes(vignette)
        ? filters.selected_vignettes.filter(v => v !== vignette)
        : [...filters.selected_vignettes, vignette]
    };
    dispatch({ type: 'SET_FILTERS', payload: newFilters });
    
    // Reset to first page when vignette filter changes
    dispatch({ type: 'SET_CURRENT_PAGE', payload: 1 });
    
    // Track vignette filter usage
    trackFilterApplied('vignette', vignette);
  }, [filters, dispatch]);

  const handleClearAllVignettes = useCallback(() => {
    const newFilters = {
      ...filters,
      selected_vignettes: []
    };
    dispatch({ type: 'SET_FILTERS', payload: newFilters });
    
    // Reset to first page when clearing all vignettes
    dispatch({ type: 'SET_CURRENT_PAGE', payload: 1 });
    
    // Track clear all vignettes
    trackFilterApplied('vignette', 'clear_all');
  }, [filters, dispatch]);

  return {
    availableData,
    handleFiltersChange,
    handleVignetteToggle,
    handleClearAllVignettes
  };
};